<template>
    <div class="module">
        <header class="module__header">
            <router-link
                tag="a"
                class="back-btn"
                data-test-id="privileged-user-back-button"
                :to="{ name: 'super-all-users' }"
            >
                <span class="icon-chevron-left" />
            </router-link>
            <h1
                class="has-back-btn"
                data-test-id="privileged-user-page-title"
            >
                {{ isEditing ? 'Edit' : 'Create' }} Privileged User
            </h1>
        </header>

        <section class="module__body">
            <form
                v-if="formLoaded"
                data-test-id="privileged-user-form"
                @submit.prevent="savePrivilegedUser"
            >
                <div class="form-flex">
                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="name">Name</label>
                            <input
                                id="name"
                                v-model="name"
                                data-test-id="privileged-user-name-field"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div class="form-group">
                            <label for="title">Title</label>
                            <input
                                id="title"
                                v-model="title"
                                data-test-id="privileged-user-title-field"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div class="form-group">
                            <label for="username">Username</label>
                            <input
                                id="username"
                                v-model="username"
                                data-test-id="privileged-user-username-field"
                                type="text"
                                required
                                maxlength="32"
                            />
                        </div>

                        <div class="form-group">
                            <label for="email">Email</label>
                            <input
                                id="email"
                                v-model="email"
                                data-test-id="privileged-user-email-field"
                                type="text"
                                required
                                maxlength="255"
                                :disabled="isEditing"
                            />
                        </div>
                    </div>
                </div>

                <div class="form-group multi-select">
                    <multi-select-field
                        :allItems="allRoles"
                        :invalid="rolesMissing"
                        label="Roles"
                        :selectedValues="selectedRoleNamesArray"
                        testId="roles-select"
                        @items-selected="handleRolesSelected"
                        @items-unselected="handleRolesUnselected"
                    />
                    <span
                        v-if="rolesMissing"
                        class="privileged-user__error"
                        data-test-id="privileged-user-roles-error"
                    >
                        Select at least one role.
                    </span>
                </div>

                <form-errors :response="errorResponse" />

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                        data-test-id="privileged-user-submit-button"
                    >
                        <div>{{ isEditing ? 'Update' : 'Create' }}</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import MultiSelectField from '@/components/MultiSelectField.vue'
import canToast from '@/mixins/canToast'
import { privilegedUserRoleOptions } from '@/constants/user-roles'

export default {
    components: {
        FormErrors,
        MultiSelectField,
    },

    mixins: [canToast],

    data() {
        return {
            id: this.$route.params.id ?? null,
            name: null,
            title: null,
            username: null,
            email: null,
            allRoles: privilegedUserRoleOptions.map(({ value, label }) => ({
                id: value,
                name: label,
            })),
            selectedRoleNames: new Set(),
            rolesMissing: false,
            errorResponse: null,
            formLoaded: false,
            submitted: false,
        }
    },

    computed: {
        isEditing() {
            return this.id !== null
        },

        selectedRoleNamesArray() {
            return Array.from(this.selectedRoleNames)
        },
    },

    mounted() {
        if (!this.isEditing) {
            this.formLoaded = true
            return
        }

        this.fetchPrivilegedUser().then(() => {
            this.formLoaded = true
        })
    },

    methods: {
        fetchPrivilegedUser() {
            return this.$http.get(`privileged-users/${this.id}`).then(({ data }) => {
                this.name = data.name
                this.title = data.title
                this.username = data.username
                this.email = data.email
                this.selectedRoleNames = new Set(data.role_names)
            })
        },

        savePrivilegedUser() {
            if (this.submitted) {
                return
            }

            this.rolesMissing = this.selectedRoleNames.size === 0

            if (this.rolesMissing) {
                return
            }

            this.submitted = true

            const data = {
                name: this.name,
                title: this.title,
                username: this.username,
                roles: this.selectedRoleNamesArray,
            }

            // The email address of an existing privileged user cannot be
            // changed, so it is only sent when creating one.
            if (!this.isEditing) {
                data.email = this.email
            }

            const request = this.isEditing
                ? this.$http.put(`privileged-users/${this.id}`, data)
                : this.$http.post('privileged-users', data)

            request
                .then(() => {
                    this.toast(this.successMessage())
                    this.$router.push({ name: 'super-all-users' })
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },

        successMessage() {
            if (this.isEditing) {
                return 'Privileged User successfully updated!'
            }

            return 'Privileged User successfully added! An email will be sent to this user to confirm their account.'
        },

        handleRolesSelected(names) {
            names.forEach((name) => {
                this.selectedRoleNames.add(name)
            })
            this.rolesMissing = false
        },

        handleRolesUnselected(names) {
            names.forEach((name) => {
                this.selectedRoleNames.delete(name)
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.privileged-user__error {
    color: #f96a62;
}
</style>
